import { useEffect, useState } from 'react';

/**
 * Auto-captured browser context snapped at the moment a bug ticket starts
 * being filled in. Used by `<TicketForm type="bug">` so the user never has
 * to type their own URL / user-agent / viewport.
 */
export interface BugContext {
  url: string;
  userAgent: string;
  viewport: string;
  appVersion: string;
}

declare global {
  // Set in the Vite config so we can stamp builds in bug reports.
  var __APP_VERSION__: string | undefined;
}

/**
 * Snap browser context once on mount. Re-render-stable — viewport changes
 * after the form opens are intentionally ignored so the report reflects
 * the state at the moment of the bug, not at the moment of submit.
 */
export function useBugContext(): BugContext {
  const [context] = useState<BugContext>(() => ({
    url: typeof window !== 'undefined' ? window.location.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    viewport:
      typeof window !== 'undefined'
        ? `${window.innerWidth}x${window.innerHeight}`
        : '',
    appVersion:
      typeof globalThis !== 'undefined' && globalThis.__APP_VERSION__
        ? globalThis.__APP_VERSION__
        : 'dev',
  }));

  // Keep the linter quiet about the unused setter — the snap is intentional
  // and we only want this to run once on mount.
  useEffect(() => {
    // no-op
  }, []);

  return context;
}
